import {
  ArrowBack,
  PersonOutline,
  PhoneOutlined,
  EmailOutlined,
  BadgeOutlined,
  CalendarTodayOutlined,
  NotificationsOutlined,
  SecurityOutlined,
  SyncAltOutlined,
  DeleteOutline,
} from '@mui/icons-material';
import { Button, CardContainer, Tag } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDeleteModal } from '@/components/admin/employees/ConfirmDeleteModal';
import { EditUserInfoModal } from '@/components/admin/employees/EditUserInfoModal';
import { ErrorState, LoadingState } from '@/components/common';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useUserDetails,
  useUpdateUser,
  useToggleUserActive,
  useDeleteUser,
} from '@/hooks/queries/useUsers';

export const UnregisteredUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: userDetails, isLoading, error } = useUserDetails(userId);
  const updateUser = useUpdateUser();
  const toggleUserActive = useToggleUserActive();
  const deleteUser = useDeleteUser();

  const handleUpdateUserInfo = async (data: {
    name?: string;
    rfc?: string;
    curp?: string;
    address?: string;
  }) => {
    if (!userId || updateUser.isPending) return;
    try {
      await updateUser.mutateAsync({ userId, data });
      setIsEditModalOpen(false);
      toast.success('Información actualizada exitosamente');
    } catch {
      toast.error('Error al actualizar la información. Intenta nuevamente.');
    }
  };

  const handleToggleActive = async () => {
    if (!userId || !userDetails) return;
    try {
      await toggleUserActive.mutateAsync({
        userId,
        data: { isActive: !userDetails.isActive },
      });
      toast.success(`Usuario ${userDetails.isActive ? 'desactivado' : 'activado'} exitosamente`);
    } catch {
      toast.error('Error al cambiar el estado del usuario. Intenta nuevamente.');
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    try {
      await deleteUser.mutateAsync(userId);
      toast.success('Usuario eliminado exitosamente');
      void navigate('/clientes/sin-organizacion');
    } catch {
      toast.error('Error al eliminar el usuario. Intenta nuevamente.');
    }
  };

  const getUserInitials = () => {
    if (!userDetails) return '??';
    if (userDetails.name.trim()) {
      return userDetails.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    }
    return userDetails.email.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <LoadingState message="Cargando información del usuario..." />
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <ErrorState title="Error al cargar el usuario" showRetry />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="pt-6">
        {/* Back button */}
        <button
          onClick={() => void navigate('/clientes/sin-organizacion')}
          className="flex items-center gap-2 text-muted hover:text-brand-white mb-6 transition-colors"
        >
          <ArrowBack fontSize="small" />
          Volver a usuarios sin organización
        </button>

        {/* User Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:!flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-semibold text-muted">{getUserInitials()}</span>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-brand-white">{userDetails.name || '—'}</h1>
                  <Tag variant={userDetails.isActive ? 'green' : 'gray'}>
                    {userDetails.isActive ? 'Activo' : 'Inactivo'}
                  </Tag>
                </div>
                <p className="text-sm text-muted">{userDetails.email}</p>
                <p className="text-xs text-muted mt-1 font-mono">ID: {userDetails.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="white"
                size="small"
                startIcon={<SyncAltOutlined />}
                onClick={() => void handleToggleActive()}
                disabled={toggleUserActive.isPending}
              >
                {userDetails.isActive ? 'Desactivar' : 'Activar'}
              </Button>
              <Button
                variant="white"
                size="small"
                startIcon={<DeleteOutline />}
                onClick={() => setIsDeleteModalOpen(true)}
                className="!text-red-400 hover:!bg-red-500/10"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Personal Information */}
            <CardContainer>
              <div>
                <div className="flex items-center justify-between pb-4 border-b border-border mb-5">
                  <div className="flex items-center gap-2">
                    <PersonOutline className="text-muted" />
                    <h3 className="text-base font-semibold text-brand-white">Información Personal</h3>
                  </div>
                  <Button variant="white" size="small" onClick={() => setIsEditModalOpen(true)}>
                    Editar
                  </Button>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-muted uppercase tracking-wide mb-1">
                      Nombre Completo
                    </p>
                    <p className="text-sm text-brand-white">{userDetails.name || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted uppercase tracking-wide mb-1">
                        RFC
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-brand-white">{userDetails.rfc || '—'}</p>
                        {userDetails.rfc && (
                          <BadgeOutlined fontSize="small" className="text-muted" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted uppercase tracking-wide mb-1">
                        CURP
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-brand-white">{userDetails.curp || '—'}</p>
                        {userDetails.curp && (
                          <BadgeOutlined fontSize="small" className="text-muted" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted uppercase tracking-wide mb-1">
                      Dirección
                    </p>
                    <p className="text-sm text-brand-white">{userDetails.address || '—'}</p>
                  </div>
                </div>
              </div>
            </CardContainer>

            {/* Contact Information */}
            <CardContainer>
              <div>
                <div className="flex items-center gap-2 pb-4 border-b border-border mb-5">
                  <PhoneOutlined className="text-muted" />
                  <h3 className="text-base font-semibold text-brand-white">Información de Contacto</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <EmailOutlined fontSize="small" className="text-muted flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-muted uppercase tracking-wide mb-0.5">
                        Email
                      </p>
                      <p className="text-sm text-brand-white">{userDetails.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <PhoneOutlined fontSize="small" className="text-muted flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-muted uppercase tracking-wide mb-0.5">
                        Teléfono
                      </p>
                      <p className="text-sm text-brand-white">
                        {userDetails.countryCode && userDetails.phoneNumber
                          ? `+${userDetails.countryCode} ${userDetails.phoneNumber}`
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContainer>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Account Status */}
            <CardContainer>
              <div>
                <div className="flex items-center gap-2 pb-4 border-b border-border mb-5">
                  <CalendarTodayOutlined className="text-muted" />
                  <h3 className="text-base font-semibold text-brand-white">Estado de Cuenta</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted">Estado</p>
                    <Tag variant={userDetails.isActive ? 'green' : 'gray'}>
                      {userDetails.isActive ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-border">
                    <p className="text-sm text-muted">Organización</p>
                    <Tag variant="gray">Sin organización</Tag>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-border">
                    <p className="text-sm text-muted">Fecha de Registro</p>
                    <p className="text-sm text-brand-white">{formatDate(userDetails.createdAt)}</p>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-border">
                    <p className="text-sm text-muted">Última Actualización</p>
                    <p className="text-sm text-brand-white">{formatDate(userDetails.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </CardContainer>

            {/* Notification & Security Settings */}
            <CardContainer>
              <div>
                <div className="flex items-center gap-2 pb-4 border-b border-border mb-5">
                  <NotificationsOutlined className="text-muted" />
                  <h3 className="text-base font-semibold text-brand-white">
                    Notificaciones y Seguridad
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    {
                      label: 'Notificaciones generales',
                      value: userDetails.notificationsEnabled,
                      icon: <NotificationsOutlined fontSize="small" className="text-muted" />,
                    },
                    {
                      label: 'Notificaciones por email',
                      value: userDetails.emailNotificationsEnabled,
                      icon: <EmailOutlined fontSize="small" className="text-muted" />,
                    },
                    {
                      label: 'Notificaciones push',
                      value: userDetails.pushNotificationsEnabled,
                      icon: <NotificationsOutlined fontSize="small" className="text-muted" />,
                    },
                    {
                      label: 'Alertas de inicio de sesión',
                      value:
                        (userDetails as { loginAlertEnabled?: boolean }).loginAlertEnabled ?? false,
                      icon: <SecurityOutlined fontSize="small" className="text-muted" />,
                    },
                    {
                      label: 'Biometría',
                      value:
                        (userDetails as { biometricsEnabled?: boolean }).biometricsEnabled ?? false,
                      icon: <SecurityOutlined fontSize="small" className="text-muted" />,
                    },
                  ].map(({ label, value, icon }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between py-2 border-b border-border last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        {icon}
                        <p className="text-sm text-muted">{label}</p>
                      </div>
                      <Tag variant={value ? 'green' : 'gray'}>
                        {value ? 'Activado' : 'Desactivado'}
                      </Tag>
                    </div>
                  ))}
                </div>
              </div>
            </CardContainer>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditUserInfoModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={data => void handleUpdateUserInfo(data)}
        userDetails={userDetails}
        isLoading={updateUser.isPending}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => void handleDelete()}
        employeeName={userDetails.name || userDetails.email}
        isLoading={deleteUser.isPending}
      />
    </div>
  );
};
